/* eslint-disable react/no-unstable-nested-components */
import type { ColumnDefinition } from 'components/SubTable';
import { SubTable } from 'components/SubTable';
import React, { useMemo } from 'react';

import { Badge } from '@fragment/ui/src/components/Badge/Badge';
import { Icon } from '@fragment/ui/src/components/Icon/BaseIcon/Icon';

export type Obligation = {
  id: string;
  name: string;
  type: 'payin' | 'payout';
  amount: number;
  paid: number;
  dueDate?: string;
  isStrikethrough?: boolean;
  isDisputed?: boolean;
};

type Props = {
  obligations: {
    payin: Obligation[];
    payout: Obligation[];
  };
  onObligationClick?: (obligation: Obligation) => void;
};

const formatCurrency = (amount: number) =>
  `$${(amount / 100).toLocaleString('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;

// Helper component for rendering paid amount in gray
const PaidAmountCell = ({ amount }: { amount: number }) => (
  <span className="text-main-500">{formatCurrency(amount)}</span>
);

export const OverviewPane = ({
  obligations,
  onObligationClick,
}: Props): JSX.Element => {
  // Define schema for obligations table
  const obligationsSchema: readonly ColumnDefinition<Obligation>[] = useMemo(
    () => [
      {
        title: 'Name',
        format: (obligation: Obligation) => obligation.name,
      },
      {
        title: 'Paid',
        justify: 'right' as const,
        format: (obligation: Obligation) => {
          const paidAmount = formatCurrency(obligation.paid);

          if (obligation.isStrikethrough) {
            return (
              <span className="line-through text-orange-500">{paidAmount}</span>
            );
          }

          // Determine which icon to show based on due date and payment status
          let icon: React.ReactElement | null = null;
          if (obligation.dueDate) {
            const today = new Date().toISOString().split('T')[0];
            const isPastDue = obligation.dueDate < today;
            const isFullyPaid = obligation.paid === obligation.amount;

            // For payouts, only show alert if more than 2 days past due
            if (obligation.type === 'payout') {
              if (!isFullyPaid && obligation.paid === 0) {
                // Calculate if current date is more than 2 days past the due date
                const dueDateTime = new Date(obligation.dueDate).getTime();
                const todayTime = new Date(today).getTime();
                const twoDaysInMs = 2 * 24 * 60 * 60 * 1000;
                const isMoreThan2DaysPastDue =
                  todayTime - dueDateTime > twoDaysInMs;

                if (isMoreThan2DaysPastDue) {
                  // Alert icon if more than 2 days past due and not fully paid
                  icon = (
                    <Icon type="alert" size="sm" backgroundColor="orange" />
                  );
                }
              } else if (isFullyPaid) {
                // Success icon if paid equals amount and due date is today or future
                icon = (
                  <Icon
                    type="success"
                    size="sm"
                    backgroundColor="asset-green"
                  />
                );
              }
            } else {
              // For payins (and other types), use original logic
              if (isPastDue && (obligation.paid === 0 || !isFullyPaid)) {
                // Alert icon if paid is 0 and due date is past, OR paid doesn't equal amount and due date is past
                icon = <Icon type="alert" size="sm" backgroundColor="orange" />;
              }
              if (isFullyPaid) {
                // Success icon if paid equals amount and due date is today or future
                icon = (
                  <Icon
                    type="success"
                    size="sm"
                    backgroundColor="asset-green"
                  />
                );
              }
            }
            // No icon if due date is today or future and paid doesn't equal amount
          }

          if (icon) {
            return (
              <Badge badge={icon} direction="rtl">
                <PaidAmountCell amount={obligation.paid} />
              </Badge>
            );
          }

          return <PaidAmountCell amount={obligation.paid} />;
        },
      },
      {
        title: 'Amount',
        justify: 'right' as const,
        format: (obligation: Obligation) => {
          const amount = formatCurrency(obligation.amount);

          if (obligation.isStrikethrough) {
            return (
              <span className="line-through text-orange-500">{amount}</span>
            );
          }
          return amount;
        },
      },
    ],
    []
  );

  const handleObligationClick = (obligation: Obligation) => {
    if (onObligationClick) {
      onObligationClick(obligation);
    }
  };

  return (
    <>
      <div className="flex">
        <div className="w-[229px] p-f1 flex">
          <span className="text-md text-main-500" />
        </div>
        <div className="w-full flex justify-between p-f1 gap-x-[1ch] text-main-500 border-l-[0.5px] border-t-[0.5px] border-r-[0.5px] border-main-300">
          <div className="w-full">Name</div>
          <div className="text-right w-full">Paid</div>
          <div className="w-full text-right">Amount</div>
        </div>
      </div>

      <div className="flex">
        <div className="w-[229px] p-f1 flex border-l-[0.5px] border-t-[0.5px] border-r-[0.5px] border-main-300">
          <Icon type="down" primaryColor="asset-green" size="md" />
          <span className="text-md text-main-500">
            {obligations.payin.length === 1 ? 'Buyer' : 'Buyers'}
          </span>
        </div>
        <div className="w-full border-r-[0.5px] border-main-300">
          <SubTable
            data={obligations.payin}
            schema={obligationsSchema}
            onClick={handleObligationClick}
            textSize="md"
            showHeader={false}
            stickyHeader={false}
            emptyValue={{
              Name: '',
              Paid: '',
              Amount: '',
            }}
          />
        </div>
      </div>

      <div className="flex">
        <div className="w-[229px] p-f1 flex border-l-[0.5px] border-t-[0.5px] border-b-[0.5px] border-r-[0.5px] border-main-300">
          <Icon type="up" primaryColor="liability-red" size="md" />
          <span className="text-md text-main-500">
            {obligations.payout.length === 1 ? 'Seller' : 'Sellers'}
          </span>
        </div>
        <div className="w-full border-r-[0.5px] border-main-300 border-b-[0.5px]">
          <SubTable
            data={obligations.payout}
            schema={obligationsSchema}
            onClick={handleObligationClick}
            textSize="md"
            showHeader={false}
            stickyHeader={false}
            emptyValue={{
              Name: '',
              Paid: '',
              Amount: '',
            }}
          />
        </div>
      </div>
    </>
  );
};
